// Shared date/time formatting for patient data tables.
//
// All patient-facing tables show the date AND the time of the reading in
// US Eastern Time, e.g. "6/12/2026 3:45PM" — month/day without leading
// zeros, 4-digit year, a single space, 12-hour clock with no leading zero,
// 2-digit minutes, and uppercase AM/PM with no space and no periods.
//
// Uses the built-in Intl API so the timezone (and DST) is handled correctly
// without pulling in moment-timezone.

// A pure date-only string ("YYYY-MM-DD" or "M/D/YYYY") has no time component,
// so it must NOT be round-tripped through Date()/Intl — that conversion can
// roll the calendar day depending on the host timezone. Re-emit its parts
// directly. Returns "M/D/YYYY" or null if `value` isn't a pure date string.
function literalDate(value) {
  if (typeof value !== 'string') return null
  const s = value.trim()
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (m) return Number(m[2]) + '/' + Number(m[3]) + '/' + m[1]
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/)
  if (m) {
    const year = m[3].length === 2 ? '20' + m[3] : m[3]
    return Number(m[1]) + '/' + Number(m[2]) + '/' + year
  }
  return null
}

function assembleET(d, withTime) {
  const opts = { timeZone: 'America/New_York', month: 'numeric', day: 'numeric', year: 'numeric' }
  if (withTime) {
    opts.hour = 'numeric'
    opts.minute = '2-digit'
    opts.hour12 = true
  }
  const parts = new Intl.DateTimeFormat('en-US', opts).formatToParts(d)
  const get = (t) => {
    const p = parts.find((x) => x.type === t)
    return p ? p.value : ''
  }
  const date = `${get('month')}/${get('day')}/${get('year')}`
  if (!withTime) return date
  return `${date} ${get('hour')}:${get('minute')}${get('dayPeriod').toUpperCase()}`
}

// "6/12/2026 3:45PM" for real timestamps. A date-only input degrades to the
// date alone (no fabricated midnight, no day shift).
export function formatDateTimeET(value) {
  if (value === null || value === undefined || value === '') return ''
  const lit = literalDate(value)
  if (lit) return lit
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d.getTime())) return typeof value === 'string' ? value : ''
  return assembleET(d, true)
}

// "6/12/2026" — date only. Use for values with no meaningful time of day.
export function formatDateET(value) {
  if (value === null || value === undefined || value === '') return ''
  const lit = literalDate(value)
  if (lit) return lit
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d.getTime())) return typeof value === 'string' ? value : ''
  return assembleET(d, false)
}
